import type {
  BlocoDocumento,
  ContextoPaginaDocumento,
} from '@/shared/components/documentos/paginado';

import type {
  CanalAuditoriaCertificado,
  DestinatarioAuditoriaCertificado,
} from '../../../types/certificadoAcessosTypes';
import { CabecalhoAuditoriaDestinatario, FatiaTabelaEventos } from '../secoes/SecaoAuditoria';

/**
 * A auditoria de um destinatário entra na paginação como um bloco por LINHA de evento, e não como
 * uma tabela atômica mais alta que a folha A4 — mesmo racional de `blocosReplicaEmail`.
 *
 * O cabeçalho do destinatário (nome, contato e a linha do tempo das tentativas) fica preso à
 * primeira linha: sem isso, o nome da parte podia terminar uma página e a tabela dela começar na
 * seguinte. Uma linha que abre página no meio da tabela repete o cabeçalho das colunas.
 *
 * Vive fora do componente de documento porque os DOIS certificados de acessos (o do processo
 * arbitral e o da sentença) montam esta mesma auditoria.
 */
export function blocosAuditoriaDestinatario(
  canal: CanalAuditoriaCertificado,
  destinatario: DestinatarioAuditoriaCertificado,
  idBase: string,
): BlocoDocumento[] {
  const { tentativas } = destinatario;
  const mostrarSubcabecalho = tentativas.length > 1;

  const linhas = tentativas.flatMap((tentativa, indiceTentativa) => {
    // Tentativa sem eventos continua exibindo o cabeçalho das colunas, como a tabela fazia.
    const eventos = tentativa.eventos.length > 0 ? tentativa.eventos : [null];
    const ultimaTentativa = indiceTentativa === tentativas.length - 1;

    return eventos.map(
      (evento, indiceEvento): BlocoDocumento => ({
        id: `${idBase}-t${indiceTentativa}-e${indiceEvento}`,
        // O subcabeçalho da tentativa ("Tentativa N · data") vai DENTRO da primeira fatia, e não
        // num bloco próprio: separado, ele podia ficar órfão no pé da página.
        render: (contexto: ContextoPaginaDocumento) => (
          <FatiaTabelaEventos
            canal={canal}
            tentativa={tentativa}
            evento={evento}
            mostrarSubcabecalho={mostrarSubcabecalho}
            primeira={indiceEvento === 0}
            ultima={indiceEvento === eventos.length - 1}
            continuacao={indiceEvento > 0 && contexto.primeiroDaPagina}
            fechaDestinatario={ultimaTentativa && indiceEvento === eventos.length - 1}
          />
        ),
      }),
    );
  });

  return [
    {
      id: `${idBase}-cabecalho`,
      manterComProximo: linhas.length > 0,
      node: <CabecalhoAuditoriaDestinatario destinatario={destinatario} />,
    },
    ...linhas,
  ];
}
